import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerOverlay } from '@chakra-ui/react';
import { t } from 'i18next';
import React, { useState } from 'react';
import { SimpleMember } from '../../domain/models/locations/SimpleMember';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryButton from '../buttons/SecondaryButton';
import PriamryCheckBox from '../checkbox/PriamryCheckBox';
import MultiSelectLikedMembersListV2 from '../lists/MultiSelectLikedMembersListV2';
import PrimarySwitch from '../switch/PrimarySwitch';

const SITE_TYPE_MENU_SCROLL_ITEMS = [
  {
    id: 'birthday_cafe',
    name: t('SiteTypes.birthdayCafe'),
    iconName: '3D.Birthday cake'
  },
  {
    id: 'billboard_ad',
    name: t('SiteTypes.billboardAd'),
    iconName: '3D.Signboard'
  },
  { id: 'experience_space', name: t('SiteTypes.experienceSpace'), iconName: '3D.Exhibition' },
  { id: 'filming_location', name: t('SiteTypes.filmingLocation'), iconName: '3D.clapper_board' },
  {
    id: 'foodie_spot',
    name: t('SiteTypes.foodieSpot'),
    iconName: '3D.Restaurant'
  },
  {
    id: 'landmark',
    name: t('SiteTypes.landmark'),
    iconName: '3D.Place'
  }
];

export interface OfflineLocationFilterBottomDrawerProps {
  isOpen: boolean;
  members: SimpleMember[];
  initialOnlyOngoingEvent?: boolean;
  onChangedOnlyOngoingEvent?: (onlyOngoingEvent: boolean) => void;
  initialSelectedMembers?: string[];
  initialSelectedSiteTypes?: string[];
  onApplyFilter?: (
    initialOnlyOngoingEvent: boolean,
    initialSelectedMembers: string[],
    initialSelectedSiteTypes: string[]
  ) => void;
  onClose: () => void;
  setIsFilterButtonActive: (active: boolean) => void; // ★ 이 줄 추가!
}

const OfflineLocationFilterBottomDrawer: React.FC<OfflineLocationFilterBottomDrawerProps> = ({
  isOpen,
  members,
  initialOnlyOngoingEvent,
  initialSelectedMembers,
  initialSelectedSiteTypes,
  onApplyFilter,
  onClose,
  setIsFilterButtonActive,
}) => {
  const [onlyOngoingEvent, setOnlyOngoingEvent] = useState<boolean>(
    initialOnlyOngoingEvent || false
  );
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(
    initialSelectedMembers || []
  );
  const [selectedSiteTypes, setSelectedSiteTypes] = useState<string[]>(
    initialSelectedSiteTypes || []
  );
  const [isAllMember, setIsAllMember] = useState<boolean>(initialSelectedMembers ? false : true);

  const initFilter = () => {
    setOnlyOngoingEvent(false);
    setIsAllMember(true);
    setSelectedMemberIds([]);
    setSelectedSiteTypes([]);
    setIsFilterButtonActive(false); // 필터버튼 비활성화!
  };

  const applyFilter = () => {
    onApplyFilter?.(onlyOngoingEvent, selectedMemberIds, selectedSiteTypes);
    onClose();
  };



  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerOverlay />
      <DrawerContent
        className="rounded-t-2xl"
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
      >
        <DrawerBody p={0}>
          {/* Filter only ongoing events */}
          <div className="flex flex-row items-center gap-[4px] px-[20px] py-[24px] border-b border-solid border-separator-light-1 dark:border-separator-dark-1">
            <PriamryCheckBox
              size="md"
              isChecked={onlyOngoingEvent}
              onChanged={setOnlyOngoingEvent}
            />
            <h3 className="text-[14px] font-semibold">{t('OfflineLocationFilterBottomDrawer.onlyOngoingEvents')}</h3>
          </div>

          {/* Members */}
          <div className="flex flex-col gap-[12px] mx-[20px] py-[24px] border-b border-solid border-separator-light-1 dark:border-separator-dark-1">
            <div className="flex flex-row items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold">{t('OfflineLocationFilterBottomDrawer.members')}</h3>
              </div>
              <div className="flex flex-row items-center gap-[4px]">
                <div className="text-[13px] font-medium text-main-light-2 dark:text-main-dark-2">{t('OfflineLocationFilterBottomDrawer.showAllMembers')}</div>
                <PrimarySwitch
                  size="sm"
                  isChecked={isAllMember}
                  onChanged={() => {
                    if (!isAllMember) {
                      setSelectedMemberIds([]);
                    }
                    setIsAllMember(!isAllMember);
                  }}
                />
              </div>
            </div>
            <MultiSelectLikedMembersListV2
              members={members}
              selectedMemberIds={selectedMemberIds}
              onMemberSelect={(member: SimpleMember) => {
                setIsAllMember(false);
                setSelectedMemberIds((prev) =>
                  prev.includes(member.memberId)
                    ? prev.filter((id) => id !== member.memberId)
                    : [...prev, member.memberId]
                );
              }}
              isAllMember={isAllMember}
              isShowHeartEmoji={true}
            />
            {/* <MultiSelectLikedMembersList
              members={members}
              onChangeLikedMembers={(members: SimpleMember[]) => {
                const memberIds = members.map((member) => member.memberId);
                if (members.length > 0) {
                  setIsAllMember(false);
                }
                setSelectedMemberIds(memberIds);
              }}
              showAllMember={true}
              isShowHeartEmoji={true}
              initialSelectedMembers={members.filter((member) =>
                selectedMemberIds.includes(member.memberId)
              )}
            /> */}
          </div>

          {/* Filter by location site type */}
          <div className="flex flex-col gap-[12px] mx-[20px] py-[24px]">
            <h3 className="text-[15px] font-semibold">{t('OfflineLocationFilterBottomDrawer.siteTypes')}</h3>
            <div className="flex flex-wrap gap-[6px]">
              {SITE_TYPE_MENU_SCROLL_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className={`flex flex-row items-center gap-[4px] rounded-[32px] p-[8px] bg-pure-white dark:bg-pure-black border border-solid ${
                    selectedSiteTypes.includes(item.id)
                      ? 'text-primary-light dark:text-primary-dark border-primary-light dark:border-primary-dark shadow-[0px_0px_4px_0px_rgba(92,61,250,0.49)]'
                      : 'text-main-light-2 dark:text-main-dark-2 border-separator-light-1 dark:border-separator-dark-1'
                  }`}
                  onClick={() =>
                    setSelectedSiteTypes((prev) =>
                      prev.includes(item.id)
                        ? prev.filter((siteTypeId) => siteTypeId !== item.id)
                        : [...prev, item.id]
                    )
                  }
                >
                  <img
                    src={`/assets/icons/graphic-3d/${item.iconName}.png`}
                    alt={item.name}
                    className="w-[14px] h-[14px]"
                  />
                  <div className="text-[13px] font-medium">{item.name}</div>
                </div>
              ))}
            </div>
          </div>
        </DrawerBody>

        <DrawerFooter className="flex flex-row items-center py-[10px] !justify-start gap-[12px] safeAreaMarginBottom">
          <SecondaryButton
            text={t('OfflineLocationFilterBottomDrawer.reset')}
            icon={null}
            leftIcon={null}
            disabled={false}
            onClick={initFilter}
            paddingX={'24px'}
            paddingY={'16px'}
            fontSize={'17px'}
            fontWeight={600}
          />

          <PrimaryButton
            text={t('OfflineLocationFilterBottomDrawer.applyFilter')}
            icon={null}
            disabled={false}
            onClick={applyFilter}
            paddingX={'24px'}
            paddingY={'16px'}
            fontSize={'17px'}
            fontWeight={600}
            flex={1}
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default OfflineLocationFilterBottomDrawer;
